import { Box, TextButton } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext, useMemo } from 'react';
import { containsUrl, HTML_TYPE } from 'ricos-content';
import { EditorContext, RicosContext } from 'ricos-context';
import type { HTMLData } from 'ricos-schema';
import { HTMLData_Source } from 'ricos-schema';
import { FloatingLayout, FooterActions } from 'wix-rich-content-ui-components';

import { EDIT_PANEL, EDIT_PANEL_CANCEL_BUTTON, EDIT_PANEL_UPDATE_BUTTON } from '../../dataHooks';
import type { AdsenseSourceInputProps } from './AdsenseSourceInput';
import { AdsenseSourceInput } from './AdsenseSourceInput';
import type { HtmlSourceInputProps } from './HtmlSourceInput';
import { HtmlSourceInput } from './HtmlSourceInput';

export type InsertModalProps = {
  nodeId: string;
  closeModal: () => void;
};

export const InsertModal: FC<InsertModalProps> = ({ nodeId, closeModal }) => {
  const { getEditorCommands } = useContext(EditorContext);
  const { isMobile } = useContext(RicosContext);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const componentData = getEditorCommands().getBlockComponentData(nodeId) as HTMLData;
  const initialComponentData = useMemo(() => componentData, []);

  const isAdsense = componentData?.isAdsense ?? componentData.source === HTMLData_Source.ADSENSE;

  const updateComponentData = (data: HTMLData) => {
    getEditorCommands().setBlock(nodeId, HTML_TYPE, {
      ...data,
      id: nodeId,
    });
  };

  const handleOnCancel = () => {
    updateComponentData(initialComponentData);
    closeModal();
  };

  const HtmlComponent: React.FC<AdsenseSourceInputProps | HtmlSourceInputProps> = isAdsense
    ? AdsenseSourceInput
    : HtmlSourceInput;
  const Footer = isMobile ? FooterActions : FooterActionsDesktop;

  return (
    <FloatingLayout
      onCloseButtonClick={handleOnCancel}
      dataHook={EDIT_PANEL}
      header={isMobile ? { title: 'Edit HTML' } : undefined}
      content={{
        noPadding: true,
        children: (
          <Box direction="vertical" gap="SP2" margin="18px 18px 3px 18px">
            <HtmlComponent
              componentData={componentData}
              updateComponentData={updateComponentData}
              inputRef={inputRef}
            />
          </Box>
        ),
      }}
      footer={{
        showDivider: isMobile,
        children: (
          <Footer
            onCancel={handleOnCancel}
            onSave={() => {
              if (componentData.url && !containsUrl(componentData.url)) {
                inputRef.current?.focus();
                return;
              }
              closeModal();
            }}
          />
        ),
      }}
    />
  );
};

const FooterActionsDesktop: React.FC<{ onCancel: () => void; onSave: () => void }> = ({
  onCancel,
  onSave,
}) => {
  const { t } = useContext(RicosContext);
  return (
    <Box align="space-between">
      <TextButton
        underline="always"
        size="small"
        dataHook={EDIT_PANEL_CANCEL_BUTTON}
        onClick={onCancel}
      >
        {t('HtmlEditPanel_Cancel')}
      </TextButton>
      <TextButton
        underline="always"
        size="small"
        dataHook={EDIT_PANEL_UPDATE_BUTTON}
        onClick={onSave}
      >
        {t('HtmlEditPanel_Update')}
      </TextButton>
    </Box>
  );
};
